import { Button, Group, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BranchSelector } from '../../components/BranchSelector';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { useModal } from '../../hooks/useModal';
import { usePagination } from '../../hooks/usePagination';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { queryWithFilterPageAndSearch } from '../../util/api/queryWithFilterPageAndSearch';
import { NewStaffMemberModal } from './components/NewStaffMemberModal';
import { StaffListRow } from './components/StaffListRow';
import type { OperatorDto } from './types/OperatorDto';
import { KeyedRow } from '../../components/KeyedRow';

export function StaffList() {
	const { organisation } = useParams();
	const pagination = usePagination();
	const filter = useFilter();
	const [searchQuery, setSearchQuery] = useState('');

	const newStaffModal = useModal();

	const operatorsCountQuery = queryWithFilterPageAndSearch<number>(
		`${organisation}/operators/count`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	const operatorsQuery = queryWithFilterPageAndSearch<OperatorDto[]>(
		`${organisation}/operators/all`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	useEffect(() => {
		document.body.style.backgroundColor = 'white';
	}, []);

	const reloadTableContents = () => {
		operatorsCountQuery.remove();
		operatorsQuery.remove();

		operatorsCountQuery.refetch();
		operatorsQuery.refetch();
	};

	return (
		<>
			<NewStaffMemberModal
				opened={newStaffModal.isOpen}
				close={newStaffModal.close}
				onAddComplete={reloadTableContents}
			/>
			<PageHeader title={'Staff List'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search usernames...'}
						onDelayComplete={(value) => {
							setSearchQuery(value);
						}}
					/>
					<BranchSelector filter={filter} />
					<Button {...whiteIconButtonStyle} onClick={() => newStaffModal.open()}>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<DataTable
				pagination={pagination}
				totalItems={operatorsCountQuery.data?.value}
				isRowsLoading={operatorsQuery.isLoading}
				isPaginationLoading={operatorsCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th w={'15vw'}>
							<Title label={'Username'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Forename'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Surname'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Job Title'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Card No.'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Branch'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Enrolled'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Clocked In'} />
						</Table.Th>
						<Table.Th>
							<Title label={'View'} />
						</Table.Th>
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{operatorsQuery.data?.value.map((operator) => (
						<StaffListRow key={operator.id} operator={operator} />
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
